import { inject, Injectable } from '@angular/core';
import { mapAsKeys } from '@sama/js-core';
import { isEqual } from 'lodash';
import { keys } from '../../../../../cross-team-shared/misc-utils/keys';
import { getOrThrow } from '../../../../../cross-team-shared/misc-utils/strict-record';
import { Selection } from '../../../../h3ml/state/fusion-state';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { ObjectScopingService } from '../../../../shared/misc-services/object-scoping.service';
import { RootUndoService } from '../../../../shared/misc-services/root-undo.service';

@Injectable()
export class ObjectSelectionService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private objectScopingService = inject(ObjectScopingService);
  private undoService = inject(RootUndoService);
  private lastSelectedObjectId?: number;

  /**
   * Set selection to the desired object ids. Does not deselect other shapes if
   * all requested objects are already selected.
   *
   * @param objectIds - The object ids that will compose the new selection. Does
   * not deselect other objects if all requested objects are already selected.
   * @returns - true if the selection changed, false otherwise
   */
  setSelection(objectIds: number[]) {
    const currentSelection = this.taskWorkspaceStore.state().objectSelection;
    if (
      objectIds.length &&
      // fix me if you change this code

      objectIds.every((objectId) => currentSelection[objectId])
    ) {
      return false;
    }

    const newSelection: Selection = mapAsKeys(objectIds, () => true);
    if (isEqual(currentSelection, newSelection)) {
      return false;
    }
    this.taskWorkspaceStore('objectSelection').set(newSelection);
    this.undoService.currentUndoState.objectSelection = newSelection;
    if (objectIds.length <= 1) {
      this.lastSelectedObjectId = objectIds[0];
    }
    return true;
  }

  /**
   * @param objectId - The id of the object to toggle
   * @returns - true if the object is now selected
   */
  toggleSelection(objectId: number) {
    const currentSelection = this.taskWorkspaceStore.state().objectSelection;
    const isSelected = currentSelection[objectId];
    // fix me if you change this code

    if (isSelected) {
      this.taskWorkspaceStore('objectSelection')(objectId).delete();
    } else {
      this.taskWorkspaceStore('objectSelection')(objectId).set(true);
      this.lastSelectedObjectId = objectId;
    }
    this.undoService.currentUndoState.objectSelection =
      this.taskWorkspaceStore.state().objectSelection;
    return !isSelected;
  }

  /**
   * @returns - selected object ids and the ids of all the objects nested in them
   */
  getNestedSelection() {
    const selectedObjectIds = keys(
      this.taskWorkspaceStore.state().objectSelection,
    );
    const nestedObjects = selectedObjectIds.flatMap((objectId) =>
      this.objectScopingService.getNestedObjects(objectId),
    );
    return [
      ...selectedObjectIds,
      ...nestedObjects.map((object) => object.index),
    ];
  }

  /**
   * @returns - the selected objects and the all the objects nested in them
   */
  getNestedSelectedObjects() {
    const nestedSelection = this.getNestedSelection();
    const { objects } = this.taskWorkspaceStore.state();
    return nestedSelection.map((id) => getOrThrow(objects, id));
  }

  getLastSelectedObjectId() {
    return this.lastSelectedObjectId;
  }
}
